import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconGlyph from './IconGlyph';
import { searchIcons } from '../utils/iconUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { RATE_ICON_EMOJIS, ICON_SEARCH_DEBOUNCE_MS } from '../constants/bookingConst';
import '../css/Modal.css';
import '../css/IconPickerModal.css';

/**
 * Picks an icon — an emoji from a ready grid, or any of Iconify's ~200k
 * open-source icons by search. Ported from the Kawnter POS category icon
 * picker, so both products choose icons the same way.
 *
 * Only the choice is saved: the emoji itself, or an Iconify id such as
 * "mdi:weather-sunny". The SVG is fetched again wherever it's displayed.
 *
 * Closes on the × or Cancel only, never on a click outside.
 *
 * @param {object} props
 * @param {string} props.title - heading, e.g. "Icon for Day rate"
 * @param {string} [props.value] - the icon currently saved
 * @param {string[]} [props.emojis] - the emoji grid; defaults to the rate icons
 * @param {(icon: string) => void} props.onSave - receives the new icon; '' clears it
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function IconPickerModal({ title, value = '', emojis = RATE_ICON_EMOJIS, onSave, onClose }) {
  const [tab, setTab] = useState('emoji');
  const [choice, setChoice] = useState(value || '');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  // The in-flight request, so an answer to an older keystroke can't arrive
  // late and overwrite the results of the newer one.
  const requestRef = useRef(null);

  useEffect(() => {
    if (tab !== 'search') return undefined;
    const query = term.trim();
    if (!query) { setResults([]); setError(''); return undefined; }

    // Debounced: one request per keystroke would be slower for the admin and
    // needlessly heavy on a free public service.
    const timer = setTimeout(async () => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      setSearching(true);
      setError('');
      try {
        setResults(await searchIcons(query, { signal: controller.signal }));
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setResults([]);
        setError(err.message);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, ICON_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, tab]);

  // Cancels anything in flight when the picker closes, so no state update
  // lands on an unmounted component.
  useEffect(() => () => requestRef.current?.abort(), []);

  /**
   * One choosable icon cell.
   * @param {string} icon
   * @param {string} label - for screen readers / hover
   * @returns {JSX.Element}
   */
  const renderCell = (icon, label) => (
    <button
      key={icon}
      type="button"
      className={`ipm-cell${choice === icon ? ' active' : ''}`}
      onClick={() => setChoice(icon)}
      title={label}
      aria-label={label}
      aria-pressed={choice === icon}
    >
      <IconGlyph icon={icon} />
    </button>
  );

  return createPortal(
    <div className="modal-overlay ipm-overlay">
      <div className="modal-container ipm-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body ipm-body">
          <div className="ipm-tabs" role="group" aria-label="Icon source">
            <button
              type="button"
              className={`ipm-tab${tab === 'emoji' ? ' active' : ''}`}
              onClick={() => setTab('emoji')}
              aria-pressed={tab === 'emoji'}
            >
              Emoji
            </button>
            <button
              type="button"
              className={`ipm-tab${tab === 'search' ? ' active' : ''}`}
              onClick={() => setTab('search')}
              aria-pressed={tab === 'search'}
            >
              Search icons
            </button>
          </div>

          {tab === 'emoji' ? (
            <div className="ipm-grid">
              {emojis.map(emoji => renderCell(emoji, emoji))}
            </div>
          ) : (
            <>
              <input
                type="search"
                className="ipm-search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="sun, moon, star…"
                aria-label="Search icons"
              />

              {searching && <div className="ipm-status">{spinnerRing('Searching icons…')}</div>}
              {error && <p className="ipm-error">{error}</p>}

              {!searching && !error && term.trim() && results.length === 0 && (
                <p className="ipm-hint">No icons match that word.</p>
              )}

              {results.length > 0 && (
                <div className="ipm-grid">
                  {results.map(id => renderCell(id, id))}
                </div>
              )}

              {!term.trim() && (
                <p className="ipm-hint">
                  Icons come from Iconify, a free open-source library. Only the icon&rsquo;s name is saved.
                </p>
              )}
            </>
          )}

          <div className="ipm-preview">
            <span className="ipm-preview-label">Preview</span>
            <span className="ipm-preview-icon">{choice ? <IconGlyph icon={choice} /> : null}</span>
            <span className="ipm-preview-value">{choice || 'No icon'}</span>
          </div>
        </div>

        <div className="modal-footer ipm-footer">
          {/* Only offered when there is something to clear. */}
          {value && (
            <button type="button" className="modal-btn modal-btn-cancel" onClick={() => onSave('')}>
              Remove icon
            </button>
          )}
          <span className="ipm-footer-spacer" />
          <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="modal-btn modal-btn-confirm modal-btn-info"
            onClick={() => onSave(choice)}
            disabled={!choice}
          >
            Use icon
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default IconPickerModal;
